import type { PartnerSchemaMappings } from '@reach/shared-types/schema-mappings';
import type { SchemaDefinition } from '../../../lib/api';

const PREVIEW_FIELD_LIMIT = 4;
const SCALAR_FIELD_TYPES = new Set(['string', 'number', 'integer', 'boolean']);
const CUSTOMER_FIELDS_CATEGORY = 'customer_fields';

interface JsonSchemaProperty {
  type?: string | string[];
  $ref?: string;
}

const getMappedSchemaIds = (mappings: PartnerSchemaMappings | undefined): Set<string> => {
  const contactsSchema = mappings?.contactsSchema;
  const contactSchemaIds = [
    contactsSchema?.primarySource.schemaId,
    ...(contactsSchema?.additionalSources ?? []).map(source => source.schemaId),
  ];
  const transactionSchemaIds = (mappings?.transactionsSchema ?? []).map(source => source.schemaId);
  const locationSchemaIds = (mappings?.locationsSchema ?? []).map(source => source.schemaId);

  return new Set(
    [...contactSchemaIds, ...transactionSchemaIds, ...locationSchemaIds].filter(
      (schemaId): schemaId is string => Boolean(schemaId),
    ),
  );
};

export const selectCustomObjectSchemas = (
  schemaDefinitions: SchemaDefinition[],
  mappings: PartnerSchemaMappings | undefined,
): SchemaDefinition[] => {
  const mappedSchemaIds = getMappedSchemaIds(mappings);

  return schemaDefinitions
    .filter(schemaDefinition => {
      const isActive = !(schemaDefinition.deactivated_at ?? schemaDefinition.deactivatedAt);
      const isStandardSchema = mappedSchemaIds.has(schemaDefinition.id);
      const isCustomerFields = schemaDefinition.category === CUSTOMER_FIELDS_CATEGORY;
      return isActive && !isStandardSchema && !isCustomerFields;
    })
    .sort((first, second) => getSchemaLabel(first).localeCompare(getSchemaLabel(second)));
};

export const getSchemaLabel = (schemaDefinition: SchemaDefinition): string =>
  schemaDefinition.plural_name || schemaDefinition.pluralName || schemaDefinition.name;

const isScalarProperty = (property: JsonSchemaProperty): boolean => {
  const propertyTypes = Array.isArray(property.type) ? property.type : [property.type];
  const hasScalarType = propertyTypes.some(propertyType => SCALAR_FIELD_TYPES.has(propertyType ?? ''));
  return hasScalarType && !property.$ref;
};

export const getExternalIdField = (schemaDefinition: SchemaDefinition): string =>
  schemaDefinition.external_id_field || schemaDefinition.externalIdField;

export const getPreviewFields = (schemaDefinition: SchemaDefinition): string[] => {
  const properties = (schemaDefinition.schema.properties ?? {}) as Record<string, JsonSchemaProperty>;
  const externalIdField = getExternalIdField(schemaDefinition);

  return Object.entries(properties)
    .filter(([fieldName, property]) => fieldName !== externalIdField && isScalarProperty(property))
    .map(([fieldName]) => fieldName)
    .slice(0, PREVIEW_FIELD_LIMIT);
};

export const formatFieldValue = (value: unknown): string => {
  const isMissing = value === undefined || value === null;
  if (isMissing) {
    return '';
  }
  return String(value);
};
